import React from 'react';

import { CursorPaginationParams } from 'types/pagination';

import { CursorPaginationProps } from 'components/Pagination/CursorPagination';

type CursorPaginationData = {
  next_cursor?: string | null;
  prev_cursor?: string | null;
};

type UseCursorPaginationOptions = {
  limit?: number;
};

export function useCursorPagination({
  limit,
}: UseCursorPaginationOptions = {}) {
  const [page, setPage] = React.useState(1);
  const [cursorParams, setCursorParams] =
    React.useState<CursorPaginationParams>({
      pagination: 'cursor',
      ...(limit !== undefined ? { limit } : {}),
    });

  const reset = React.useCallback(() => {
    setPage(1);
    setCursorParams((prev) => ({
      ...prev,
      next_cursor: undefined,
      prev_cursor: undefined,
    }));
  }, []);

  function getProps(
    data: CursorPaginationData | undefined,
    isFetching: boolean
  ): CursorPaginationProps {
    return {
      page,
      hasNextPage: Boolean(data?.next_cursor),
      hasPreviousPage: Boolean(data?.prev_cursor),
      onNextPage: () => {
        if (!data?.next_cursor || isFetching) return;
        setPage((prev) => prev + 1);
        setCursorParams((prev) => ({
          ...prev,
          next_cursor: data.next_cursor!,
          prev_cursor: undefined,
        }));
      },
      onPreviousPage: () => {
        if (!data?.prev_cursor || isFetching) return;
        setPage((prev) => Math.max(1, prev - 1));
        setCursorParams((prev) => ({
          ...prev,
          prev_cursor: data.prev_cursor!,
          next_cursor: undefined,
        }));
      },
      disabled: isFetching,
    };
  }

  return { page, cursorParams, reset, getProps };
}
